import React, { useMemo, useState, createContext } from 'react';
import {
  AlertService,
  Button,
  Modal,
  PencilIcon,
  Search,
  Select,
  SelectOption,
  ColorBadge,
  TextInput,
  Toggle,
  TypeAhead,
} from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import { useMutation, useQuery } from '@apollo/client';
import { propTypes, siteRoles, styles } from './site-editing-helpers';
import { AdminBSRQuery, SiteNameTypeAheadQuery } from '../../../graphql/queries';
import { VColumn, VRowAction, VTable } from '../../../components/v-table';
import { UpdateBuyerSellerRelationship } from '../../../graphql/mutations';
import EmailList from '../../../components/email-list/email-list';

const useStyles = createUseStyles(styles);

const Relationships = ({ site }) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState('');
  const [editingBSR, setEditingBSR] = useState(null);
  const [bsrTypeFilter, setBsrTypeFilter] = useState([{ value: 'SEED', text: 'SEED' }]);

  const currentSiteRole = useMemo(() => {
    if (site?.siteRole.includes('SELLER')) return siteRoles.SELLER;
    if (site?.siteRole.includes('BUYER')) return siteRoles.BUYER;
    return null;
  }, [site.siteRole]);

  const queryVars = useMemo(() => {
    if (currentSiteRole === siteRoles.SELLER) return { sellerSiteId: site._id };
    if (currentSiteRole === siteRoles.BUYER) return { buyerSiteId: site._id };
    return {};
  }, [currentSiteRole, site._id]);

  const { data, loading } = useQuery(AdminBSRQuery, {
    variables: queryVars,
  });
  const [updateBuyerSellerRelationship, { loading: saving }] = useMutation(UpdateBuyerSellerRelationship);

  const seedBSRs = useMemo(() => {
    if (data?.adminBSRQuery) {
      const values = bsrTypeFilter.map((f) => f.value);
      return data.adminBSRQuery.filter((bsr) => values.includes(bsr.type));
    }
    return [];
  }, [data, bsrTypeFilter]);

  const bsrColumns = useMemo(() => {
    const cols = [
      new VColumn({
        field: 'name',
        displayName: 'Name',
        sortable: true,
      }),
      new VColumn({
        field: 'type',
        displayName: 'Type',
        sortable: true,
      }),
      new VColumn({
        field: 'buyerUserEmails',
        displayName: 'Buyer Emails',
        sortable: true,
      }),
      new VColumn({
        field: 'sellerUserEmails',
        displayName: 'Seller Emails',
        sortable: true,
      }),
    ];

    const nameFormatter = (value, row) => (
      <span>
        <span>{value}</span> {row.archived ? <ColorBadge theme="grey">Archived</ColorBadge> : ''}
      </span>
    );

    if (site.siteRole.includes('SELLER')) {
      cols.unshift(
        new VColumn({
          field: 'buyerSiteName',
          displayName: 'Buyer',
          sortable: true,
          defaultSort: true,
          defaultSortDirection: 'asc',
          formatter: nameFormatter,
        }),
      );
    } else if (site.siteRole.includes('BUYER')) {
      cols.unshift(
        new VColumn({
          field: 'sellerSiteName',
          displayName: 'Seller',
          sortable: true,
          defaultSort: true,
          defaultSortDirection: 'asc',
          formatter: nameFormatter,
        }),
      );
    }

    return cols;
  }, [site.siteRole]);

  const editingBSRValid = useMemo(() => {
    if (!editingBSR) return false;
    if (editingBSR.sellerSiteName === '') return false;
    if (editingBSR.buyerSiteName === '') return false;
    if (editingBSR.bsrName === '') return false;
    if (editingBSR.bsrType.length !== 1) return false;
    if (!editingBSR.touched) return false;
    return true;
  }, [editingBSR]);

  const saveBuyerSellerRelationship = () => {
    if (!editingBSRValid) return false;
    updateBuyerSellerRelationship({
      variables: {
        buyerSiteName: editingBSR.buyerSiteName,
        sellerSiteName: editingBSR.sellerSiteName,
        buyerEmails: editingBSR.buyerEmails,
        sellerEmails: editingBSR.sellerEmails,
        bsrName: editingBSR.bsrName,
        bsrType: editingBSR.bsrType[0].value,
        editBsr: editingBSR.editBsr,
        originalBsrName: editingBSR.originalBsrName,
        archived: editingBSR.archived,
      },
    }).then((res) => {
      setEditingBSR(null);
      AlertService.alert({
        type: 'success',
        message: res.data.updateBuyerSellerRelationship.result,
        autoDismiss: true,
        dismissDelay: 5000,
      });
    });
    return true;
  };

  const handleSearch = (text) => {
    setSearchText(text);
  };

  const ownEmailList = useMemo(() => {
    return [
      ...seedBSRs.reduce((emails, bsr) => {
        let list = bsr.sellerUserEmails;
        if (currentSiteRole === siteRoles.BUYER) list = bsr.buyerUserEmails;
        list.forEach((email) => emails.add(email.toLowerCase()));
        return emails;
      }, new Set()),
    ];
  }, [currentSiteRole, seedBSRs]);

  const handleAddBSR = () => {
    let buyerSiteName = '';
    let sellerSiteName = '';
    let buyerEmails = [];
    let sellerEmails = [];
    if (currentSiteRole === siteRoles.BUYER) {
      buyerSiteName = site.siteName;
      buyerEmails = ownEmailList;
    }
    if (currentSiteRole === siteRoles.SELLER) {
      sellerSiteName = site.siteName;
      sellerEmails = ownEmailList;
    }
    setEditingBSR({
      editBsr: false,
      buyerSiteName,
      sellerSiteName,
      buyerEmails,
      sellerEmails,
      bsrName: '',
      bsrType: [{ value: 'SEED', text: 'SEED' }],
      originalBsrName: '',
      archived: false,
      touched: false,
    });
  };

  const handleEditBSR = (row) => {
    const { _id, sellerSiteName, buyerSiteName, name, buyerUserEmails, sellerUserEmails, type, archived } = row;
    setEditingBSR({
      _id,
      buyerSiteName,
      sellerSiteName,
      buyerEmails: buyerUserEmails,
      sellerEmails: sellerUserEmails,
      bsrName: name,
      bsrType: [{ value: type, text: type }],
      originalBsrName: name,
      editBsr: true,
      archived,
      touched: false,
    });
  };

  const handleBSRUpdate = (field, value) => {
    const _bsr = { ...editingBSR };
    if (!_bsr.touched) _bsr.touched = true;
    _bsr[field] = value;
    if (field === 'sellerSiteName' || field === 'bsrType' || field === 'buyerSiteName') {
      _bsr.bsrName = `${_bsr.sellerSiteName} - ${_bsr.buyerSiteName} - ${_bsr.bsrType[0].value}`;
    }
    setEditingBSR(_bsr);
  };

  const handleTypeAheadUpdate = (field, value) => {
    handleBSRUpdate(field, value.displayText);
  };

  return (
    <div>
      <div className={classes.header}>
        <div>
          <Search onChange={handleSearch} className={classes.search} />
        </div>
        <Select
          label="BSR Type"
          multiple
          selectedItems={bsrTypeFilter}
          onChange={(values) => setBsrTypeFilter([...values])}
        >
          <SelectOption value="SEED">SEED</SelectOption>
          <SelectOption value="OFFER">OFFER</SelectOption>
          <SelectOption value="AWARD">AWARD</SelectOption>
        </Select>
        <div className={classes.headerRight}>
          <Button onClick={handleAddBSR}>Add BSR</Button>
        </div>
      </div>
      <div className={classes.vtable}>
        <VTable
          searchText={searchText}
          cols={bsrColumns}
          data={seedBSRs}
          loading={loading}
          height={600}
          turnOffAutoHeight={true}
          rowActions={[
            <VRowAction
              secondary
              key="edit"
              icon={PencilIcon}
              onClick={(row) => () => handleEditBSR(row)}
              tooltipText="Delete BSR"
            />,
          ]}
        />
      </div>
      <Modal open={!!editingBSR} onHide={() => setEditingBSR(null)}>
        {editingBSR && (
          <>
            <div className={classes.modalContent}>
              <h2 className={classes.modalHeading}>{editingBSR.editBsr ? 'Edit' : 'Add'} Relationship</h2>
              <div className={classes.formbox}>
                <div className={classes.half}>
                  <TextInput
                    onChange={handleBSRUpdate.bind(this, 'bsrName')}
                    type="text"
                    labelText="Name"
                    value={editingBSR.bsrName}
                  />
                </div>
                <div className={classes.half}>
                  {currentSiteRole === siteRoles.SELLER && (
                    <TypeAhead
                      query={SiteNameTypeAheadQuery}
                      queryName="siteNameTypeAheadQuery"
                      dataFormat={[{ displayText: 'buyerSiteName' }]}
                      labelText={'Buyer Site'}
                      value={editingBSR.buyerSiteName}
                      onChange={handleTypeAheadUpdate.bind(this, 'buyerSiteName')}
                      itemLimit={200}
                      queryVariables={{ siteRole: siteRoles.BUYER, siteType: 'ORGANIZATION' }}
                      autoWidth
                    />
                  )}
                  {currentSiteRole === siteRoles.BUYER && (
                    <TypeAhead
                      query={SiteNameTypeAheadQuery}
                      queryName="siteNameTypeAheadQuery"
                      dataFormat={[{ displayText: 'sellerSiteName' }]}
                      labelText={'Seller Site'}
                      value={editingBSR.sellerSiteName}
                      onChange={handleTypeAheadUpdate.bind(this, 'sellerSiteName')}
                      itemLimit={200}
                      queryVariables={{ siteRole: siteRoles.SELLER, siteType: 'ORGANIZATION' }}
                      autoWidth
                    />
                  )}
                </div>
                <div className={classes.half}>
                  <Select
                    label="BSR Type"
                    selectedItems={editingBSR.bsrType}
                    onChange={handleBSRUpdate.bind(this, 'bsrType')}
                  >
                    <SelectOption value="SEED">SEED</SelectOption>
                    <SelectOption value="OFFER">OFFER</SelectOption>
                    <SelectOption value="AWARD">AWARD</SelectOption>
                  </Select>
                </div>
                <div className={classes.half}>
                  <Toggle
                    labelText="Archived"
                    value={editingBSR.archived}
                    onClick={handleBSRUpdate.bind(this, 'archived', !editingBSR.archived)}
                  />
                </div>
                <EmailList
                  label="Seller Emails"
                  list={editingBSR?.sellerEmails || []}
                  onChange={handleBSRUpdate.bind(this, 'sellerEmails')}
                />
                <EmailList
                  label="Buyer Emails"
                  list={editingBSR?.buyerEmails || []}
                  onChange={handleBSRUpdate.bind(this, 'buyerEmails')}
                />
              </div>

              <div className={classes.modalFooter}>
                <Button secondary onClick={() => setEditingBSR(null)}>
                  Cancel
                </Button>
                <Button
                  loading={saving}
                  loadingText="Saving..."
                  onClick={saveBuyerSellerRelationship}
                  disabled={!editingBSRValid}
                >
                  {editingBSR.editBsr ? 'Update BSR' : 'Save New BSR'}
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

Relationships.propTypes = propTypes;

export default Relationships;
